/**
 * 짐 목록 화면 — v2 문서 버전.
 *
 * 프로덕션에서는 `MovingItemsFlowAdapter` 가 spec 을 받아 FlowDocument 를 통째로
 * 조립한다. 문서로 남는 게 없어 편집자가 볼 수도 고칠 수도 없었고, 프리뷰에는 아예
 * 목업 데이터만 있었다.
 *
 * 여기서는 데이터도 화면도 선언이다.
 *   - `data` 가 스펙을 불러 섹션으로 나눈다 (`MOVING_ITEM_SECTIONS_DATA`)
 *   - 화면은 그 섹션을 반복해서 그린다
 *
 * 그리는 쪽은 프로덕션과 같은 `ItemPicker` 다. 아이콘은 스펙의 `iconDownloadUrl` 이
 * 항목의 `icon` 으로 흘러가고, ItemPicker 가 URL 로 해석해 띄운다.
 */
import type { AssetDocument, DataSource } from "@miso/design-system";

import { MOVING_ITEM_SECTIONS_DATA } from "../data/movingItems";

/**
 * 목록 위에 붙는 서비스 범위 탭. 계단 3단(운반만 ⊂ 분해/운반 ⊂ 분해/운반/설치)
 * 다음에 특수 2개가 온다 — 프로덕션 `SERVICE_MODE_OPTIONS` 와 같은 순서·라벨이다.
 * 모드를 고르면 그 서비스가 짐마다 기본값으로 들어가고, 지원하지 않는 짐은 흐려진다.
 */
const SERVICE_MODES: Array<{ id: string; label: string; group: string }> = [
  { id: "TRANSPORT_ONLY", label: "운반만", group: "ladder" },
  { id: "DISASSEMBLY_TRANSPORT", label: "분해/운반", group: "ladder" },
  {
    id: "DISASSEMBLY_TRANSPORT_INSTALLATION",
    label: "분해/운반/설치",
    group: "ladder",
  },
  { id: "TRANSPORT_TO_FIRST_FLOOR", label: "1층으로 운반", group: "special" },
  { id: "DISPOSAL", label: "버림", group: "special" },
];

/**
 * 어느 방을 기록하는가.
 *
 * 이 문서는 두 자리에서 열린다. 방 선택 화면에서 카드를 눌러 오면 그 화면이 이미
 * `targetRoomId`·`roomLabel` 을 정해 넘긴다. 반면 현관은 사용자가 고르지 않고 규칙이
 * 지목하므로(`stage.target`), 그 경우에만 여기서 편다 — 물려받은 값이 이겨서, 고른
 * 방이 규칙이 지목한 방으로 덮이는 일은 없다.
 */
const TARGET_DATA: DataSource[] = [
  {
    id: "_target",
    derive: "counsel.recordTarget",
    args: {
      target: { $var: "target" },
      targetRoomId: { $var: "targetRoomId" },
      targetSessionId: { $var: "targetSessionId" },
      roomLabel: { $var: "roomLabel" },
      roomType: { $var: "roomType" },
    },
    spread: true,
  },
];

/** 방 영상 질문 id. 업로드 액션이 이 값을 읽는다. */
export const ROOM_VIDEO_QUESTION_ID = "roomVideo";

const doc: AssetDocument = {
  id: "counsel.movingItems",
  schemaVersion: 2,
  initial: "pick",
  data: [...TARGET_DATA, ...MOVING_ITEM_SECTIONS_DATA],
  steps: {
    pick: {
      kind: "screen",
      /*
       * 고른 짐이 방 하나의 기록이 된다. 프로덕션은 세션을 열고(`startRoomObserving
       * Session`) 항목을 기록한 뒤 방을 완료 처리한다 — 그 세 호출이 어댑터의
       * `onComplete` 에 있었다. 추가공간이면 호스트가 대응하는 세션 API 로 보낸다.
       */
      onSubmit: [
        {
          do: "callAction",
          name: "startRoomObservingSession",
          args: {
            roomId: { $var: "targetRoomId" },
            sessionId: { $var: "targetSessionId" },
          },
        },
        {
          do: "callAction",
          name: "recordRoomObservingMovingItems",
          args: {
            roomId: { $var: "targetRoomId" },
            sessionId: { $var: "targetSessionId" },
            movingItems: { $var: "selectedItems" },
          },
        },
        { do: "submit" },
      ],
      /*
       * 짐만 적고 끝나지 않는다. 프로덕션(`MovingItemsFlowAdapter`)은 같은 흐름 안에서
       * 방 영상까지 찍고 나서야 세션을 닫는다 — 리뷰 화면이 방마다 "짐 & 영상" 을 세는
       * 것도 그래서다. 문서에서 영상을 빼 두면 프리뷰에서만 방이 짐으로 끝나 버린다.
       */
      layout: {
        type: "Screen",
        /*
         * 프로덕션 카피 그대로다(`MovingItemsFlowAdapter`). 방 이름이 제목에 들어가야
         * 여러 방을 이어 기록할 때 지금 어느 방인지 알 수 있다.
         */
        props: {
          variant: "question",
          // 앱바 제목은 비워 둔다. 목록이 위로 붙는 순간 방 이름이 앱바로 올라온다
          // (collapsing header) — 프로덕션과 같은 동작이라 picker 가 직접 채운다.
          title: "{{roomLabel}}에서 가져가실 짐을\n선택해주세요",
        },
        // 앱바 뒤로가기 = 방 선택으로 돌아가기. 방을 잘못 골랐을 때 빠져나갈 길이다.
        on: { onBack: [{ do: "callAction", name: "movingItems.back" }] },
        slots: {
          footer: [
            {
              type: "CtaButton",
              props: { label: "다음" },
              on: { onPress: [{ do: "submit" }] },
            },
          ],
        },
        children: [
          /*
           * 스펙을 못 불러온 경우를 화면에 남긴다. 목록이 비었을 때 "짐이 없다" 와
           * "데이터가 안 붙었다" 는 완전히 다른 상황인데, 둘 다 빈 화면으로 보이면
           * 프리뷰에서 구분할 방법이 없다.
           */
          {
            type: "Card",
            visibleWhen: { isEmpty: { var: "specs" } },
            children: [
              {
                type: "Group",
                props: { padded: true, tight: true },
                children: [
                  {
                    type: "Text",
                    props: { text: "짐 스펙을 불러오지 못했습니다.", strong: true },
                  },
                  {
                    type: "Text",
                    props: {
                      text: "프리뷰에 스펙 API 자격증명이 설정되지 않았을 수 있습니다.",
                      tone: "muted",
                    },
                  },
                ],
              },
            ],
          },
          /*
           * 프로덕션과 같은 ItemPicker 를 그대로 쓴다. 섹션·검색·선택 리뷰·sub-flow 시트가
           * 붙어 있는 덩어리라 비슷하게 다시 그리면 프리뷰에서 본 것과 실제 화면이 다른
           * 물건이 된다. 문서가 정하는 건 어떤 섹션을 보여줄지 뿐이다.
           */
          {
            type: "ItemPicker",
            bind: "selectedItems",
            // 스펙이 비면 derive 는 빈 "전체 품목" 섹션 하나를 돌려준다. 그걸 그리면
            // 못 불러온 상태가 "짐이 없는 방" 처럼 보이므로 통째로 감춘다.
            visibleWhen: { not: { isEmpty: { var: "specs" } } },
            props: {
              sections: { $var: "sections" },
              roomLabel: { $var: "roomLabel" },
              multiple: true,
              // 짐이 없는 방도 있다. 막지 않되 한 번 되묻는다.
              allowEmptyProceed: true,
              reviewTitle: "선택한 짐 목록",
              reviewButtonLabel: "짐 확인하기",
              emptyTitle: "{{roomLabel}}에서 가져가실 짐이 없어요",
              emptyDescription:
                "{{roomLabel}}에서 가져가실 짐이 없어요.\n짐 없이 진행하시겠어요?",
              modeAnswerKey: "__serviceMode",
              modes: {
                defaultModeId: "DISASSEMBLY_TRANSPORT_INSTALLATION",
                label: "서비스 범위",
                options: SERVICE_MODES,
                presetAnswers: Object.fromEntries(
                  SERVICE_MODES.map((m) => [m.id, { serviceSet: m.id }]),
                ),
              },
            },
          },
        ],
      },
      next: { kind: "goto", target: "recordVideo" },
    },

    /*
     * 방 영상. 프로덕션과 같은 `video-full` 전체화면이라 제목도 CTA 도 없다 — 촬영
     * 컨트롤이 화면의 전부이고, 다 찍은 뒤 `업로드` 가 이 화면을 끝낸다.
     *
     * 업로드는 스케치 영상과 같은 3단(토큰 → 스토리지 → 세션 반영)이되 마지막이
     * 다르다. 방은 세션을 닫아야(`completeRoomObservingSession`) 목록에서 완료로
     * 바뀐다.
     */
    recordVideo: {
      kind: "screen",
      layout: {
        type: "QuestionSequence",
        children: [
          {
            type: "Question",
            bind: ROOM_VIDEO_QUESTION_ID,
            props: {
              layout: "video-full",
              // 앱바 제목은 템플릿을 받지 않는다(v1 질문 스키마가 문자열). 방 이름은
              // 제목 쪽에서 편다.
              appBarTitle: "방 영상",
              title: { template: "{{roomLabel}} 영상" },
            },
            children: [
              {
                type: "Input.video-recorder",
                bind: ROOM_VIDEO_QUESTION_ID,
                props: {
                  required: true,
                  preferUltraWide: true,
                  defaultFacingMode: "environment",
                  autoRequestPermission: true,
                },
              },
            ],
          },
        ],
      },
      onSubmit: [
        {
          do: "callAction",
          name: "video.requestUploadToken",
          args: {
            resourceType: "VIDEO",
            subType: "VISIT_COUNSEL_VIDEO_CLIP",
            video: { $var: ROOM_VIDEO_QUESTION_ID },
          },
          resultAs: "upload",
        },
        {
          do: "callAction",
          name: "video.upload",
          args: {
            upload: { $var: "upload" },
            video: { $var: ROOM_VIDEO_QUESTION_ID },
          },
        },
        {
          do: "callAction",
          name: "recordRoomObservingVideo",
          args: {
            roomId: { $var: "targetRoomId" },
            sessionId: { $var: "targetSessionId" },
            videoToken: { $var: "upload.structuredToken" },
          },
        },
        {
          do: "callAction",
          name: "completeRoomObservingSession",
          args: {
            roomId: { $var: "targetRoomId" },
            sessionId: { $var: "targetSessionId" },
          },
        },
        { do: "toast", tone: "success", text: "{{roomLabel}} 기록이 저장되었습니다" },
        { do: "submit" },
      ],
      next: { kind: "exit" },
    },
  },
};

/**
 * 프리뷰용 기본값. 방 라벨은 프로덕션에서 상담 컨텍스트가 넘겨주는 값이라
 * 문서에 박지 않고 호스트가 채우는 자리로 남긴다.
 */
const initialValues = { roomType: "ROOM", roomLabel: "안방", selectedItems: [] };

export const MOVING_ITEMS = { doc, initialValues };
